import { useCallback, useMemo, useEffect } from "react"
import { useNavigate, useOutletContext } from "react-router-dom"
import { useDirection } from "hooks/direction/useDirection"
import { Outlet } from "react-router-dom"
import { ErrorDialog } from "components/Dialog"
import { useTranslation } from "providers/i18n"
import { Head } from "../../../../../components/Head"
import { getFeatureDetail } from "utils/feature"
import { useGeoLocation } from "providers/geolocation"
import { useCountdownTimer } from "hooks/useCountdownTimer"

export const DirectionLayoutKioskVPage = () => {
  const {
    route,
    isError,
    origin,
    destination,
    isCalculatingRoute,
    isNavigating,
    exitPath,
    elevatorPreferredMode,
    toggleElevatorPreference,
  } = useDirection()
  const { deviceLocation } = useGeoLocation()
  const { t } = useTranslation({ defaultValue: null })
  const { name: destinationName = "" } = useMemo(
    () => (destination ? getFeatureDetail(destination) : {}),
    [destination]
  )

  const { name: originName = "" } = useMemo(
    () => (origin ? getFeatureDetail(origin) : {}),
    [origin]
  )
  const { scannedLocations, userLocation } = useOutletContext()
  const navigate = useNavigate()

  const handleCloseDialog = useCallback(() => {
    navigate(exitPath)
  }, [navigate, exitPath])

  const { countdownTimer } = useCountdownTimer({
    onComplete: handleCloseDialog,
    options: {
      enable: isError,
    },
  })

  useEffect(() => {
    if (!origin && destination && deviceLocation) {
      /**
       * Auto-assign predefined user location as origin if missing.
       *
       * Redirect rules:
       * - If step index exists → redirect to step 1.
       * - Otherwise → redirect to direction overview page.
       */
      const stepPath = isNavigating ? "/navigate/steps/1" : ""
      navigate(
        `/maps/dir/${deviceLocation.properties.userLocationParam}/${destination?.id}${stepPath}`,
        { replace: true }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, origin, deviceLocation, navigate])

  return (
    <>
      <Head
        title={`${t("title.direction", {
          origin: t(originName),
          destination: t(destinationName),
        })}`}
      />
      <ErrorDialog
        open={isError}
        title={t("Navigation Error", { ns: "error" })}
        description={t(
          "The system was unable navigate because either the origin or the destination was not found which you have chosen.",
          { ns: "error" }
        )}
        onClose={handleCloseDialog}
        buttonText={`${t("Close")} (${countdownTimer})`}
      />
      {
        <Outlet
          context={
            route
              ? {
                  ...route,
                  route,
                  userLocation,
                  scannedLocations,
                  elevatorPreferredMode,
                  isCalculatingRoute,
                  toggleElevatorPreference,
                }
              : {
                  route,
                  origin,
                  destination,
                  scannedLocations,
                  elevatorPreferredMode,
                  isCalculatingRoute,
                  toggleElevatorPreference,
                }
          }
        />
      }
    </>
  )
}
